/**
 * Blog Editor Page
 * 
 * Page for creating and editing blog posts.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/Publish';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useBlogEditor } from '../../hooks/useBlogEditor';
import { adminBlogService } from '../../services/adminBlogService';
import MarkdownEditor from '../../components/admin/MarkdownEditor';
import FrontmatterForm from '../../components/admin/FrontmatterForm';
import ProtectedRoute from '../../components/admin/ProtectedRoute';
import Breadcrumbs from '../../components/admin/Breadcrumbs';
import { useNotifications } from '../../context/NotificationContext';

const BlogEditor: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(!!id);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const isEditMode = !!id;

  const {
    blog,
    setBlog,
    updateField,
    isSaving,
    error,
    saveDraft,
    publish,
    deleteBlog,
  } = useBlogEditor();
  const { showSuccess, showError } = useNotifications();

  /**
   * Load blog post for editing
   */
  useEffect(() => {
    if (id) {
      const loadBlog = async () => {
        setIsLoading(true);
        try {
          const blogData = await adminBlogService.getBlogForEdit(id);
          // Convert to BlogPostInput format
          // Use rawContent if available (from API), otherwise try to extract from content
          const rawContent = blogData.rawContent || blogData.content || '';
          
          setBlog({
            title: blogData.title,
            slug: blogData.slug || blogData._id,
            content: rawContent, // Use raw markdown content for editing
            excerpt: blogData.excerpt || '',
            publishedDate: blogData.publishedDate
              ? blogData.publishedDate.split('T')[0]
              : new Date().toISOString().split('T')[0],
            lastModified: blogData.lastModified,
            featuredImage: blogData.featuredImage,
            categories: blogData.categories || [],
            tags: blogData.tags || [],
            isFeatured: blogData.isFeatured || false,
            status: blogData.status || (blogData.isPublished ? 'published' : 'draft'),
            publishedAt: blogData.publishedAt || (blogData.isPublished ? blogData.publishedDate : null),
            readingTime: blogData.readingTime,
            seo: blogData.seo,
            _id: blogData._id, // Store ID for updates
          } as any);
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to load blog post';
          showError(errorMessage);
        } finally {
          setIsLoading(false);
        }
      };

      loadBlog();
    }
  }, [id, setBlog, showError]);

  /**
   * Handle save draft
   */
  const handleSaveDraft = async () => {
    const saved = await saveDraft();
    if (saved) {
      showSuccess('Blog post saved as draft');
      navigate('/admin/blog');
    } else if (error) {
      showError(error);
    }
  };

  /**
   * Handle publish
   */
  const handlePublish = async () => {
    const saved = await publish();
    if (saved) {
      showSuccess('Blog post published successfully');
      navigate('/admin/blog');
    } else if (error) {
      showError(error);
    }
  };

  /**
   * Handle delete
   */
  const handleDelete = async () => {
    if (id) {
      try {
        await deleteBlog(id);
        setDeleteDialogOpen(false);
        showSuccess('Blog post deleted successfully');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete blog post';
        showError(errorMessage);
      }
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ProtectedRoute>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          items={[
            { label: 'Admin', path: '/admin' },
            { label: 'Blog Management', path: '/admin/blog' },
            { label: isEditMode ? 'Edit Post' : 'New Post' },
          ]}
        />

        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/blog')}
          >
            Back to List
          </Button>
          <Typography variant="h4" component="h1">
            {isEditMode ? 'Edit Blog Post' : 'Create New Blog Post'}
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Frontmatter Form */}
        <FrontmatterForm blog={blog} updateField={updateField} />

        {/* Markdown Editor */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Content
          </Typography>
          <MarkdownEditor
            value={blog.content}
            onChange={(value) => updateField('content', value)}
            minHeight={500}
          />
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {isEditMode && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isSaving}
            >
              Delete
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSaveDraft}
            disabled={isSaving || !blog.title || !blog.content}
          >
            {isSaving ? <CircularProgress size={24} /> : 'Save Draft'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PublishIcon />}
            onClick={handlePublish}
            disabled={isSaving || !blog.title || !blog.content}
          >
            {isSaving ? <CircularProgress size={24} /> : 'Publish'}
          </Button>
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Blog Post</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete "{blog.title}"? This action
              cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </ProtectedRoute>
  );
};

export default BlogEditor;

